<template>
  <div class="node-list-table-wrapper">
    <!-- 折叠按钮 -->
    <div class="collapse-btn-wrapper">
      <a-button shape="circle" :icon="visible ? 'down' : 'up'" type="primary" @click="handleToggle" size="small" />
    </div>

    <!-- 列表内容 -->
    <div class="node-list-content" v-show="visible">
      <a-card :bordered="false" size="small">
        <!-- 筛选和操作栏 -->
        <div class="table-header">
          <a-row :gutter="8">
            <a-col :span="8">
              <a-select
                v-model="queryParam.reviewStatus"
                :placeholder="$t('审核状态')"
                size="small"
                style="width: 100%"
                @change="loadData"
                allowClear
              >
                <a-select-option :value="10">{{ $t('草稿') }}</a-select-option>
                <a-select-option :value="20">{{ $t('审核中') }}</a-select-option>
                <a-select-option :value="30">{{ $t('审核通过') }}</a-select-option>
                <a-select-option :value="40">{{ $t('审核失败') }}</a-select-option>
              </a-select>
            </a-col>
            <a-col :span="8">
              <a-button type="primary" icon="search" size="small" @click="loadData" :loading="loading"> {{ $t('查询') }} </a-button>
              <a-button icon="reload" size="small" @click="searchReset" style="margin-left: 8px"> {{ $t('重置') }} </a-button>
            </a-col>
          </a-row>
        </div>

        <!-- 表格 -->
        <a-table
          :columns="columns"
          :data-source="dataSource"
          :pagination="paginationOptions"
          :loading="loading"
          rowKey="id"
          :scroll="{ y: 300 }"
          size="small"
          @change="handleTableChange"
          bordered
          :customRow="handleRowClick"
        >
          <!-- 节点类型列 -->
          <template slot="type" slot-scope="text, record">
            {{ filterDictTextByCache('node_type', record.type) }}
          </template>

          <!-- 审核状态列 -->
          <template slot="reviewStatus" slot-scope="text, record">
            <a-tag :color="getStatusColor(record.reviewStatus)">
              {{ getStatusText(record.reviewStatus) }}
            </a-tag>
          </template>

          <!-- 操作列 -->
          <template slot="action" slot-scope="text, record">
            <!-- 草稿状态 (10) 和 审核失败状态 (40) -->
            <template v-if="record.reviewStatus === 10 || record.reviewStatus === 40">
              <a-button type="link" size="small" @click="handleSubmit(record)">{{ $t('提交') }}</a-button>
              <a-divider type="vertical" />
              <a-button type="link" size="small" @click="handleEdit(record)">{{ $t('编辑') }}</a-button>
              <a-divider type="vertical" />
              <a-button type="link" size="small" @click="handleDelete(record)">{{ $t('删除') }}</a-button>
            </template>

            <!-- 审核中状态 (20) -->
            <template v-else-if="record.reviewStatus === 20">
              <a-button type="link" size="small" @click="handleRollback(record)">{{ $t('撤回申请') }}</a-button>
              <!-- 只有非创建者才能进行审核操作 -->
              <template v-if="record.createBy !== $store.getters.userInfo.username">
                <a-divider type="vertical" />
                <a-button type="link" size="small" @click="handleApprove(record, 1)">{{ $t('通过') }}</a-button>
                <a-divider type="vertical" />
                <a-button type="link" size="small" @click="handleApprove(record, 0)">{{ $t('驳回') }}</a-button>
              </template>
            </template>

            <!-- 审核通过状态 (30) -->
            <template v-else-if="record.reviewStatus === 30">
              <a-button type="link" size="small" @click="handleUpdateApply(record)">{{ $t('更新申请') }}</a-button>
              <a-divider type="vertical" />
              <a-button type="link" size="small" @click="handleDeleteApply(record)">{{ $t('删除申请') }}</a-button>
            </template>
          </template>
        </a-table>
      </a-card>
    </div>
  </div>
</template>

<script>
import { getAction, httpAction, deleteAction } from '@/api/manage'
import { filterDictTextByCache } from '@/components/dict/JDictSelectUtil'

export default {
  name: 'NodeListTable',
  data() {
    return {
      visible: true,
      loading: false,
      dataSource: [],
      queryParam: {
        reviewStatus: undefined,
        projectCode: '',
        topologyCode: '',
      },
      deleteRemark: '', // 删除申请的备注
      ipagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '30', '50'],
      },
      url: {
        list: '/jeecg-gis/topology/node/list',
        submit: '/jeecg-gis/topology/node/submit',
        updateDraft: '/jeecg-gis/topology/node/updateDraft',
        deleteDraft: '/jeecg-gis/topology/node/deleteDraft',
        updateApply: '/jeecg-gis/topology/node/updateApply',
        deleteApply: '/jeecg-gis/topology/node/deleteApply',
        rollbackApply: '/jeecg-gis/topology/node/rollbackApply',
      },
    }
  },
  computed: {
    // 分页（含国际化 showTotal）
    paginationOptions() {
      return {
        ...this.ipagination,
        showTotal: total => this.$t('共 {total} 条', { total }),
      }
    },
    columns() {
      return [
        {
          title: this.$t('编号'),
          dataIndex: 'id',
          width: 120,
          align: 'center',
        },
        {
          title: this.$t('名称'),
          dataIndex: 'name',
          width: 150,
          ellipsis: true,
          align: 'center',
        },
        {
          title: this.$t('节点类型'),
          dataIndex: 'type',
          scopedSlots: { customRender: 'type' },
          width: 120,
          align: 'center',
        },
        {
          title: this.$t('审核状态'),
          dataIndex: 'reviewStatus',
          scopedSlots: { customRender: 'reviewStatus' },
          width: 100,
          align: 'center',
        },
        {
          title: this.$t('创建人'),
          dataIndex: 'createBy',
          width: 100,
          ellipsis: true,
          align: 'center',
        },
        {
          title: this.$t('创建时间'),
          dataIndex: 'createTime',
          width: 150,
          align: 'center',
        },
        {
          title: this.$t('操作'),
          dataIndex: 'action',
          scopedSlots: { customRender: 'action' },
          width: 200,
          align: 'center',
        },
      ]
    },
  },
  created() {
    // 从配置中获取 projectCode 和 topologyCode
    this.loadTopologyParams()
    this.loadData()
  },
  methods: {
    filterDictTextByCache,

    // 加载拓扑参数
    loadTopologyParams() {
      const topologyData = this.$ls.get('GIS_TOPOLOGY_DATA')
      if (topologyData && topologyData.topology) {
        this.queryParam.projectCode = topologyData.topology.projectCode
        this.queryParam.topologyCode = topologyData.topology.code
      }
    },

    // 折叠/展开切换
    handleToggle() {
      this.visible = !this.visible
    },

    // 加载数据
    loadData() {
      if (!this.queryParam.projectCode || !this.queryParam.topologyCode) {
        this.$message.warning(this.$t('未获取到拓扑参数，请稍后重试'))
        return
      }

      this.loading = true
      const params = {
        ...this.queryParam,
        pageNo: this.ipagination.current,
        pageSize: this.ipagination.pageSize,
      }

      getAction(this.url.list, params)
        .then(res => {
          if (res.success) {
            this.dataSource = res.result.records || []
            this.ipagination.total = res.result.total || 0
          } else {
            this.$message.error(res.message || this.$t('加载数据失败'))
          }
        })
        .finally(() => {
          this.loading = false
        })
    },

    // 表格变化事件
    handleTableChange(pagination) {
      this.ipagination.current = pagination.current
      this.ipagination.pageSize = pagination.pageSize
      this.loadData()
    },

    // 行点击事件 - 高亮地图上对应设备
    handleRowClick(record) {
      return {
        on: {
          click: () => {
            this.$emit('highlightNode', record)
          }
        }
      }
    },

    // 重置搜索
    searchReset() {
      this.queryParam.reviewStatus = undefined
      this.ipagination.current = 1
      this.loadData()
    },

    // 获取状态颜色
    getStatusColor(status) {
      const colorMap = {
        10: 'blue', // 草稿 - 蓝色
        20: 'orange', // 审核中 - 橙色
        30: 'green', // 审核通过 - 绿色
        40: 'red', // 审核失败 - 红色
      }
      return colorMap[status] || 'default'
    },

    // 获取状态文本
    getStatusText(status) {
      const textMap = {
        10: this.$t('草稿'),
        20: this.$t('审核中'),
        30: this.$t('审核通过'),
        40: this.$t('审核失败'),
      }
      return textMap[status] || this.$t('未知')
    },

    // 提交审核
    handleSubmit(record) {
      this.$confirm({
        title: this.$t('确认提交'),
        content: this.$t('确定要提交节点 "{name}" 的审核申请吗？', { name: record.name }),
        onOk: () => {
          return httpAction(this.url.submit + '?id=' + record.id + '&izPass=1', {}, 'POST').then(res => {
            if (res.success) {
              this.$message.success(this.$t('提交成功'))
              this.loadData()
            } else {
              this.$message.error(res.message || this.$t('提交失败'))
            }
          })
        },
      })
    },

    // 编辑草稿
    handleEdit(record) {
      // 触发父组件的编辑事件，传递节点信息和操作类型
      this.$emit('editNode', {
        project_code: record.projectCode,
        topology_code: record.topologyCode,
        code: record.code,
        name: record.name,
        reviewStatus: record.reviewStatus,
        operationType: 'edit', // 标识为编辑操作
      })
    },

    // 删除草稿
    handleDelete(record) {
      this.$confirm({
        title: this.$t('确认删除'),
        content: this.$t('确定要删除节点 "{name}" 吗？', { name: record.name }),
        onOk: () => {
          return deleteAction(this.url.deleteDraft, { id: record.id }).then(res => {
            if (res.success) {
              this.$message.success(this.$t('删除成功'))
              this.loadData()
            } else {
              this.$message.error(res.message || this.$t('删除失败'))
            }
          })
        },
      })
    },

    // 撤回申请
    handleRollback(record) {
      this.$confirm({
        title: this.$t('确认撤回'),
        content: this.$t('确定要撤回节点 "{name}" 的申请吗？', { name: record.name }),
        onOk: () => {
          return httpAction(this.url.rollbackApply + '?id=' + record.id + '&izPass=1', {}, 'POST').then(res => {
            if (res.success) {
              this.$message.success(this.$t('撤回成功'))
              this.loadData()
            } else {
              this.$message.error(res.message || this.$t('撤回失败'))
            }
          })
        },
      })
    },

    // 通过或驳回审核
    handleApprove(record, flag) {
      const isPass = flag === 1
      this.$confirm({
        title: isPass ? this.$t('确认通过') : this.$t('确认驳回'),
        content: isPass
          ? this.$t('确定要通过节点 "{name}" 的审核吗？', { name: record.name })
          : this.$t('确定要驳回节点 "{name}" 的审核吗？', { name: record.name }),
        onOk: () => {
          return httpAction(this.url.submit + '?id=' + record.id + '&izPass=' + flag, {}, 'POST').then(res => {
            if (res.success) {
              this.$message.success(isPass ? this.$t('审核通过') : this.$t('已驳回'))
              this.loadData()
            } else {
              this.$message.error(res.message || this.$t('操作失败'))
            }
          })
        },
      })
    },

    // 更新申请
    handleUpdateApply(record) {
      // 打开编辑弹框，传递操作类型为 updateApply
      this.$emit('editNode', {
        project_code: record.projectCode,
        topology_code: record.topologyCode,
        code: record.code,
        name: record.name,
        operationType: 'updateApply', // 标识为更新申请操作
      })
    },

    // 删除申请
    handleDeleteApply(record) {
      // 显示自定义弹框，让用户输入备注
      this.$confirm({
        title: this.$t('删除申请'),
        content: h => {
          return h('div', [
            h('p', this.$t('确定要删除节点 "{name}" 的申请吗？', { name: record.name })),
            h('a-textarea', {
              props: {
                placeholder: this.$t('请输入删除备注'),
                rows: 4,
                value: this.deleteRemark,
              },
              on: {
                input: e => {
                  this.deleteRemark = e.target.value
                },
              },
            }),
          ])
        },
        width: 500,
        onOk: () => {
          // 验证备注是否已填写
          if (!this.deleteRemark || this.deleteRemark.trim() === '') {
            this.$message.warning(this.$t('请输入删除备注'))
            return Promise.reject()
          }

          // 调用删除接口，传递备注
          return deleteAction(this.url.deleteApply, {
            id: record.id,
            remark: this.deleteRemark,
          }).then(res => {
            if (res.success) {
              this.$message.success(this.$t('删除申请成功'))
              this.deleteRemark = '' // 清空备注
              this.loadData()
            } else {
              this.$message.error(res.message || this.$t('删除申请失败'))
            }
          })
        },
        onCancel: () => {
          this.deleteRemark = '' // 取消时清空备注
        },
      })
    },
  },
}
</script>

<style scoped>
.node-list-table-wrapper {
  position: relative;
}

.collapse-btn-wrapper {
  position: absolute;
  top: -30px;
  right: 20px;
  z-index: 1000;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  padding: 5px;
}

.node-list-content {
  margin-top: 10px;
}

.table-header {
  margin-bottom: 12px;
}
</style>
